import { useState, useEffect } from 'react';
import { AxiosResponse } from 'axios';
import NMINewCardForm from './NMINewCardForm';
import SavedCardForm from './SavedCardForm';
import ApiService from '../../services/ApiService';
import { Invoice, PaymentDepartment, CardTransaction } from './types';
import CardPayment from './CardPayment';

// test cards
// {
//   visa: '4000000000000002',
//   visaCorporate: '4159288888888882',
//   masterCard: '5121212121212124',
//   discover: '6011000000000004',
//   dinersClub12: '36111111111111',
//   dinersClub16: '3811112222222222',
//   americanExpress: '370000000000002',
//   jcb: '3566664444444445',
//   ecg: '6032610007325520', // Electronic Gift Card
//   fcCAD: '4032769999999992',
//   fcEUR: '5432675555555552',
// }

interface Props {
  amount?: number;
  customerNum?: string;
  invoices: Invoice[];
  department: PaymentDepartment;
  onSuccess: (saleTx: CardTransaction) => void;
  onDeclined: (saleTx: CardTransaction) => void;
  onError: (response: any) => void;
  sessionId?: string;
  values?: {
    firstName?: string;
    lastName?: string;
    amount?: string;
    number?: string;
    expiration?: string;
    cvv?: string;
    address?: string;
    zip?: string;
  };
}

const NMIPaymentForm = ({
  amount,
  invoices,
  customerNum,
  department,
  sessionId,
  values = {},
  onError,
  onDeclined,
  onSuccess,
}: Props) => {
  const [view, setView] = useState('saved');
  const [cards, setCards] = useState();

  if (amount) {
    values.amount = String(amount);
  }

  useEffect(() => {
    ApiService.get(`customer/${customerNum}/cards`).then((response: AxiosResponse) => {
      setCards(response.data || []);
      if (!response.data || !response.data.length) {
        setView('new');
      }
    });
  }, []);

  const handleCardSelect = ({ token, amount, card }: any) => {
    return ApiService.post('nmi/sale', {
      customerNum,
      invoices,
      customerVaultId: token,
      department,
      amount,
      card,
      sessionId,
    }).then((response: AxiosResponse) => {
      const { sale } = response.data;
      const payment = new CardPayment(sale);

      if (payment.isApproved()) {
        onSuccess(sale);
      } else {
        onDeclined(sale);
      }
    });
  };

  const handleCardCollect = async ({ token, amount, billing, card, saveCard = 'Y' }: any) => {
    let response;
    let customerVaultId: string = '';

    try {
      response = await ApiService.post('nmi/customer', { token, billing });
      customerVaultId = response.data.customer_vault_id;
    } catch (err) {
      console.error(err);
    }

    return ApiService.post('nmi/sale', {
      customerNum,
      invoices,
      customerVaultId,
      department,
      amount,
      billing,
      token,
      card,
      sessionId,
    })
      .then((response: AxiosResponse) => {
        const { sale } = response.data;
        const payment = new CardPayment(sale);

        if (payment.isApproved()) {
          onSuccess({ ...sale, saveCard });
        } else {
          onDeclined(sale);
        }
      })
      .catch(onError);
  };

  if (!cards) {
    return null;
  }

  return (
    <>
      <div className="toolbar-wrapper">
        <div className="default-toolbar">
          <div className="left-toolbar">
            <div
              className={`modal-button has-icon view-toggle ${view === 'saved' ? 'selected' : ''}`}
              onClick={() => setView('saved')}
              title="Saved Cards"
            >
              <i className="fas fa-credit-card" />
              <span className="modal-button-label">Saved Cards</span>
            </div>
            <div
              className={`modal-button has-icon view-toggle ${view === 'new' ? 'selected' : ''}`}
              onClick={() => setView('new')}
              title="New Card"
            >
              <i className="fas fa-plus-circle" />
              <span className="modal-button-label">New Card</span>
            </div>
          </div>
        </div>
      </div>
      {view === 'saved' && (
        <SavedCardForm
          customerNum={customerNum}
          amount={values.amount || '0'}
          cards={cards}
          onSelect={handleCardSelect}
        />
      )}
      {view === 'new' && <NMINewCardForm values={values} onSubmit={handleCardCollect} />}
    </>
  );
};

export default NMIPaymentForm;
